import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "./i18n";
import { sortClasses } from "./lib/classOrder";

interface ClassOption {
  id: string;
  name: string;
  role: "teaching" | "homeroom";
}

export default function ClassSwitcher({
  items,
  value,
  onChange,
}: {
  items: ClassOption[];
  value: string;
  onChange: (id: string) => void;
}) {
  const { t } = useTranslation("classroomInterface");
  const id = useId();
  const root = useRef<HTMLSpanElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const optionElements = useRef(new Map<string, HTMLButtonElement>());
  const typed = useRef({ text: "", time: 0 });
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const groups = [
    { role: "teaching", label: t("classTeaching") },
    { role: "homeroom", label: t("classHomeroom") },
  ] as const;
  const ordered = groups.flatMap((group) => sortClasses(items.filter((item) => item.role === group.role)));
  const selected = ordered.find((item) => item.id === value);
  const activeItem = ordered[active];

  useEffect(() => {
    if (!open) return;
    const closeOutside = (event: PointerEvent) => {
      if (event.target instanceof Node && !root.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", closeOutside);
    return () => document.removeEventListener("pointerdown", closeOutside);
  }, [open]);

  useEffect(() => {
    if (open && activeItem) optionElements.current.get(activeItem.id)?.scrollIntoView({ block: "nearest" });
  }, [open, activeItem]);

  function expand() {
    if (!ordered.length) return;
    setActive(
      Math.max(
        0,
        ordered.findIndex((item) => item.id === value),
      ),
    );
    typed.current = { text: "", time: 0 };
    setOpen(true);
  }

  function select(item: ClassOption) {
    setOpen(false);
    trigger.current?.focus();
    if (item.id !== value) onChange(item.id);
  }

  return (
    <span
      className="class-switcher"
      ref={root}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
      }}
    >
      <button
        type="button"
        role="combobox"
        className="class-switcher-trigger"
        ref={trigger}
        aria-label={t("classSelect")}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={open ? `${id}-list` : undefined}
        aria-activedescendant={open && activeItem ? `${id}-${activeItem.id}` : undefined}
        onClick={() => (open ? setOpen(false) : expand())}
        onKeyDown={(event) => {
          if (event.key === "Escape" || event.key === "Tab") {
            setOpen(false);
            if (event.key === "Escape" && open) event.preventDefault();
            return;
          }
          if (["ArrowDown", "ArrowUp", "Home", "End", "Enter", " "].includes(event.key)) {
            event.preventDefault();
            if (!open) {
              expand();
              return;
            }
            if (event.key === "Enter" || event.key === " ") {
              if (activeItem) select(activeItem);
            } else if (event.key === "Home") setActive(0);
            else if (event.key === "End") setActive(ordered.length - 1);
            else setActive(Math.max(0, Math.min(ordered.length - 1, active + (event.key === "ArrowDown" ? 1 : -1))));
            return;
          }
          if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
            event.preventDefault();
            const now = Date.now();
            const text = (now - typed.current.time < 700 ? typed.current.text : "") + event.key.toLowerCase();
            typed.current = { text, time: now };
            const index = ordered.findIndex((item) => item.name.toLowerCase().startsWith(text));
            if (index >= 0) {
              setOpen(true);
              setActive(index);
            }
          }
        }}
      >
        <span className="class-switcher-label">{selected?.name || t("classSelect")}</span>
        <svg aria-hidden="true" viewBox="0 0 16 16" width="16" height="16" className="class-switcher-chevron">
          <path
            d="m4 6 4 4 4-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open && (
        <span id={`${id}-list`} className="class-switcher-menu" role="listbox" aria-label={t("classMenu")}>
          {groups.map((group) => {
            const options = ordered.filter((item) => item.role === group.role);
            if (!options.length) return null;
            return (
              <span role="group" aria-label={group.label} className="class-switcher-group" key={group.role}>
                <span className="class-switcher-group-label" aria-hidden="true">
                  {group.label}
                </span>
                {options.map((item) => (
                  <button
                    type="button"
                    role="option"
                    id={`${id}-${item.id}`}
                    key={item.id}
                    ref={(element) => {
                      if (element) optionElements.current.set(item.id, element);
                      else optionElements.current.delete(item.id);
                    }}
                    className={`class-switcher-option${activeItem?.id === item.id ? " is-highlighted" : ""}`}
                    tabIndex={-1}
                    aria-label={item.name}
                    aria-selected={item.id === value}
                    onMouseDown={(event) => event.preventDefault()}
                    onMouseMove={() => setActive(ordered.findIndex((option) => option.id === item.id))}
                    onClick={() => select(item)}
                  >
                    <span className="class-switcher-check" aria-hidden="true">
                      {item.id === value ? "✓" : ""}
                    </span>
                    <span className="class-switcher-option-name">{item.name}</span>
                  </button>
                ))}
              </span>
            );
          })}
        </span>
      )}
    </span>
  );
}
